import { Typography, Grid, Button, Box, Tooltip } from '@mui/material'
import { FC, ReactNode, useEffect, useState } from 'react'
import { Search, SlipsScreenType } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { searchSlipsAction } from '../../../store/slips'
import { Bill } from '../../../services'
import { DatePicker } from '@mui/x-date-pickers'
import EditIcon from '@mui/icons-material/Edit'

export interface SlipPageHeaderProps {
  screenType: SlipsScreenType
  bill?: Bill
  onDateSelect: (date: Date) => void
  subheaderInfo: ReactNode
  /** When true, header EDIT SITE is disabled (e.g. Site Info tab is active). */
  disableEditSite?: boolean
  /** Opens the same Edit Site dialog as elsewhere on View Slips (e.g. E-Way GST prompt). */
  onEditSiteClick?: () => void
}
export let currentSiteId = 0
export const SlipPageHeader: FC<SlipPageHeaderProps> = ({
  screenType,
  onDateSelect,
  subheaderInfo,
  bill,
  disableEditSite = false,
  onEditSiteClick,
}) => {

  const dispatchAction = useAppDispatch()
  const searchTerm = useAppSelector((state) => state.manageSlips.searchTerm)
  const [date, setDate] = useState<Date | null>(() => new Date())
  const screenTitle =
    screenType === SlipsScreenType.Account
      ? ''
      : screenType === SlipsScreenType.Date
        ? `Daily Register`
        : 'Parties'


  const handleSearch = (searchTerm) => {
    dispatchAction(searchSlipsAction(searchTerm))
  }

  const truncateText = (text: string | ReactNode, maxLength =30): string | ReactNode => {
    if (typeof text !== 'string') {
      return text
    }
    if (text.length <= maxLength) {
      return text
    }
    // Truncate to maxLength - 3 (for ellipsis) + ellipsis = maxLength total
    return text.substring(0, maxLength - 3) + '...'
  }

  useEffect(() => {
    if(bill) {
      currentSiteId = bill.id
    }
    return () => {
      currentSiteId = 0
    }
  }, [bill])

  const displayText = subheaderInfo ? subheaderInfo : screenTitle
  const truncatedText = truncateText(displayText)
  const siteAddressLine =
    screenType === SlipsScreenType.Account && bill?.siteAddress
      ? String(bill.siteAddress).trim()
      : ''

  return (
    <>
      <Grid container alignItems="center">
        
        <Grid item xs>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
        >
            <Typography data-cy='id_typo_page_title' variant="h3" component="h3" sx={{ pl: 0, lineHeight: 1.2 }}>
            {truncatedText}
            </Typography>
            {siteAddressLine ? (
              <Typography
                component="div"
                variant="body2"
                data-cy="id_typo_site_address"
                sx={{
                  pl: 0,
                  mt: 0.25,
                  lineHeight: 1.25,
                  color: 'text.secondary',
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateText(siteAddressLine, 55)}
              </Typography>
            ) : null}
          </Box>
         
        </Grid>

        <Grid item sx={{ width: '30%' }}>
          <Search onSearch={handleSearch} value={searchTerm ?? ''} />
        </Grid>
        <Grid item sx={{ width: 190, textAlign: 'right' }}>
          {screenType === SlipsScreenType.Date && (
            <div
              style={{
                borderRadius: 4,
                height: 41.5,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <DatePicker
                sx={{ ml: 2 }}
                value={date}
                onChange={(newValue) => {
                  setDate(newValue)
                  onDateSelect(newValue)
                }}
                slotProps={{ textField: { size: 'small' } }}
                format="dd/MM/yyyy"
              />
            </div>
          )}
          {screenType === SlipsScreenType.Account &&
            (disableEditSite ? (
              <Tooltip title="Use the Site Info tab to edit this site.">
                <span>
                  <Button
                    startIcon={<EditIcon />}
                    variant="contained"
                    disabled
                    sx={{ backgroundColor: '#37A239', height: 41.5, width: 180 }}
                  >
                    EDIT SITE
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                sx={{ backgroundColor: '#37A239', height: 41.5, width: 180 }}
                onClick={() => {
                  onEditSiteClick?.()
                }}
              >
                EDIT SITE
              </Button>
            ))}
        </Grid>
      </Grid>
    </>
  )
}

export default SlipPageHeader
